import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import ScheduleDayCard from "../../../ScheduleDayCard";
import DisciplineActivityCard, {
  DisciplineMoveContentParams,
} from "../../../DisciplineActivityCard";
import { ScheduleDayEntry, ScheduleDayContent } from "../../../../../model/ScheduleHub";
import { useUpdateHubWeekSchedule } from "../../../../../hooks/ScheduleHub/useUpdateHubWeekSchedule";
import { IScheduleArrays } from "../../../../../model/ScheduleHub";
import { useNavigate } from "react-router-dom";
import { add, addHours, format } from "date-fns";

import {
  shouldShowScheduleItemChangeModal,
  showScheduleItemChangeModal,
} from "../ItemChangeModal";

interface IScheduleList {
  actualWeek?: IScheduleArrays;
  cronogramaId?: string;
  scheduleDays: ScheduleDayEntry[];
  setScheduleDays: Dispatch<SetStateAction<ScheduleDayEntry[]>>;
  onCardClick: (day: string, discipline: string, week: number) => void;
  onScheduleChange?: () => void;
}

const ScheduleList: React.FC<IScheduleList> = ({ actualWeek, cronogramaId, scheduleDays, setScheduleDays, onCardClick, onScheduleChange }) => {
  const navigate = useNavigate();
  const { mutate } =  useUpdateHubWeekSchedule()

  const [activeDragActivity, setActiveDragActivity] = useState<ScheduleDayContent | null>(null);
  const didDragRef = useRef(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    didDragRef.current = true;
    const data = event.active.data.current as { activity: ScheduleDayContent } | undefined;

    if(data?.activity.isCompleted) {
      didDragRef.current = false;
      return;
    }
    
    if (data?.activity) setActiveDragActivity(data.activity);
  }, []);

  const handleCardClick = useCallback((activity: ScheduleDayContent & { day: string; week: number }) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    onCardClick(activity.day, activity.discipline, activity.week);
  }, [onCardClick]);

  const handleMoveContentConfirm = useCallback(
    (params: DisciplineMoveContentParams) => {
      const { fromDate, toDate, discipline, oldDate, sourceActivityIndex } =
        params;

      if (!cronogramaId || !fromDate) {
        return Promise.reject(new Error("Dados insuficientes para mover conteúdo."));
      }

      const sourceDayKey = fromDate;
      const targetDayKey = format(toDate, "yyyy-MM-dd");

      return new Promise<void>((resolve, reject) => {
        mutate(
          {
            idCronograma: cronogramaId,
            fromDate: new Date(`${sourceDayKey}T00:00:00`),
            toDate,
            discipline,
            oldDate: oldDate ? new Date(`${oldDate}T00:00:00`) : undefined,
          },
          {
            onSuccess: () => {
              if (sourceDayKey !== targetDayKey) {
                setScheduleDays((prev) => {
                  const sourceDay = prev.find((d) => d.dayKey === sourceDayKey);
                  const targetDay = prev.find((d) => d.dayKey === targetDayKey);

                  if (!sourceDay) {
                    return prev;
                  }

                  const activityIndex =
                    typeof sourceActivityIndex === "number" &&
                    sourceActivityIndex >= 0 &&
                    sourceActivityIndex < sourceDay.contents.length
                      ? sourceActivityIndex
                      : sourceDay.contents.findIndex(
                          (content) => content.discipline === discipline
                        );

                  if (activityIndex < 0) {
                    return prev;
                  }

                  const movedActivity = sourceDay.contents[activityIndex];
                  const newSourceActivities = sourceDay.contents.filter(
                    (_, index) => index !== activityIndex
                  );
                  const movedActivityWithUpdatedDate = {
                    ...movedActivity,
                    // originalData: addHours(new Date(`${sourceDayKey}T00:00:00`), 3),
                  };

                  return prev.map((day) => {
                    if (day.dayKey === sourceDayKey) {
                      return { ...day, contents: newSourceActivities };
                    }

                    if (targetDay && day.dayKey === targetDayKey) {
                      return {
                        ...day,
                        contents: [...day.contents, movedActivityWithUpdatedDate],
                      };
                    }

                    return day;
                  });
                });
              }

              resolve();
            },
            onError: (error) => {
              reject(error as Error);
            },
          }
        );
      });
    },
    [cronogramaId, mutate, setScheduleDays]
  );

  const performMove = useCallback(
    (sourceDayKey: string, targetDayKey: string, activityIndex: number, activity: ScheduleDayContent) => {
      if (cronogramaId) {
        mutate(
          {
            idCronograma: cronogramaId,
            fromDate: add(new Date(sourceDayKey), { hours: 3 }),
            toDate: add(new Date(targetDayKey), { hours: 3 }),
            discipline: activity.discipline,
            oldDate: activity.originalData,
          },
          {
            onSuccess: () => {
              onScheduleChange?.();
            },
          }
        );
      }

      setScheduleDays((prev) => {
        const sourceDay = prev.find((d) => d.dayKey === sourceDayKey);
        const targetDay = prev.find((d) => d.dayKey === targetDayKey);
        if (!sourceDay || !targetDay) return prev;

        const newSourceActivities = sourceDay.contents.filter((_, i) => i !== activityIndex);
        
        const activityWithUpdatedDate: ScheduleDayContent = {
          ...activity,
          // originalData: addHours(new Date(sourceDayKey), 3),
        };

        const newTargetActivities = [...targetDay.contents, activityWithUpdatedDate];

        return prev.map((d) => {
          if (d.dayKey === sourceDayKey) {
            return {
              ...d,
              contents: newSourceActivities
            };
          }
          if (d.dayKey === targetDayKey) {
            return {
              ...d,
              contents: newTargetActivities
            };
          }
          return d;
        }) as ScheduleDayEntry[];
      });
    },
    [cronogramaId, mutate, setScheduleDays, onScheduleChange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragActivity(null);
      const { active, over } = event;

      if (!over) return;

      const data = active.data.current as
        | { sourceDayKey: string; activityIndex: number; activity: ScheduleDayContent }
        | undefined;
      if (!data) return;

      const { sourceDayKey, activityIndex, activity } = data;
      const overId = String(over.id);
      const targetDayKey = overId.length > 10 ? overId.slice(0, 10) : overId;
      if (sourceDayKey === targetDayKey) return;

      if (!shouldShowScheduleItemChangeModal()) {
        performMove(sourceDayKey, targetDayKey, activityIndex, activity);
        return;
      }

      showScheduleItemChangeModal().then((confirmed: boolean) => {
        if (confirmed) {
          performMove(sourceDayKey, targetDayKey, activityIndex, activity);
        }
      });
    },
    [performMove]
  );

  const onDayManagement = ({
    dayKey,
    week,
  }: {
    date: Date;
    dayKey: string;
    week: number;
  }) => {
    if (!cronogramaId || !week || !dayKey) return;

    const query = new URLSearchParams({
      semana: week.toString(),
      dia: dayKey,
      editMode: "true",
    });

    navigate(`/plano-adaptativo/${cronogramaId}/disciplinas?${query.toString()}`);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-row items-center gap-3 overflow-x-auto w-full">
        {scheduleDays.map((entry) => (
          <ScheduleDayCard
            actualWeek={actualWeek}
            key={entry.dayKey}
            dayKey={entry.dayKey}
            day={entry.dayName}
            date={entry.date}
            totalHours={entry.time}
            activities={entry.contents}
            onEditActivities={onDayManagement}
            onActivityCardClick={handleCardClick}
            onMoveContentSuccess={onScheduleChange}
            onMoveContentConfirm={handleMoveContentConfirm}
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeDragActivity ? (
          <div className="rotate-2 scale-105 shadow-lg rounded-md cursor-grabbing">
            <DisciplineActivityCard
              compact
              discipline={activeDragActivity.discipline}
              progress={activeDragActivity.progress}
              isRevision={activeDragActivity.isRevision}
              isCompleted={activeDragActivity.isCompleted}
              showMoveContentAction={false}
              originalDay={activeDragActivity.originalData ? format(addHours(new Date(activeDragActivity.originalData), 3), 'yyyy-MM-dd') : undefined}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ScheduleList;
